import { type QueryFilters, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  deleteResultsFile,
  restoreResultsFile,
  updateResultsFileTags,
} from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { buildUploadResultsInvalidations, queryKeys, useInvalidateQueries } from '@/shared/hooks';
import {
  gridPaths,
  invalidateExecutionRuns,
  invalidateGridQuery,
  invalidateResultsFiles,
} from '@/shared/hooks/queryKeys';
import type { ResultsFileDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
  slug: string;
};

type UseResultsFilesTableRowActionsParams = {
  selectedProjectId: string | null;
};

type UseResultsFilesTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: ResultsFileDto) => void;
  onRestoreRow: (row: ResultsFileDto) => void;
  restoreLoading: boolean;
  restoreRowPendingId: string | null;
  onUpdateRowTagsColor: (input: {
    row: ResultsFileDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useResultsFilesTableRowActions = ({
  selectedProjectId,
}: UseResultsFilesTableRowActionsParams): UseResultsFilesTableRowActionsResult => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [restoreRowPendingId, setRestoreRowPendingId] = useState<string | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteResultsFileMutation = useMutation({
    mutationFn: async (input: { slug: string }) =>
      await deleteResultsFile(input, selectedOrganisationId),
    onSuccess: async (_deleted, input) => {
      await invalidateQueries(
        buildUploadResultsInvalidations({
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
        }),
      );
      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'results file',
          name: deleteTarget?.name ?? input.slug,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const restoreResultsFileMutation = useMutation({
    mutationFn: async (input: { slug: string }) =>
      await restoreResultsFile(input, selectedOrganisationId),
    onSuccess: async (_restored, input) => {
      await invalidateQueries(
        buildUploadResultsInvalidations({
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
        }),
      );
      notify({
        message: getToastMessage('entity.restored', { entity: 'results file', name: input.slug }),
        tone: 'green',
      });
      setRestoreRowPendingId(null);
    },
    onError: (error) => {
      setRestoreRowPendingId(null);
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: ResultsFileDto) => {
    setDeleteTarget({ id: row.id, name: row.fileName, slug: row.slug });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteResultsFileMutation.isPending) return;
    setDeleteTarget(null);
  }, [deleteResultsFileMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteResultsFileMutation.mutate({ slug: deleteTarget.slug });
  }, [deleteResultsFileMutation, deleteTarget]);

  const onRestoreRow = useCallback(
    (row: ResultsFileDto) => {
      setRestoreRowPendingId(row.id);
      restoreResultsFileMutation.mutate({ slug: row.slug });
    },
    [restoreResultsFileMutation],
  );

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: ResultsFileDto; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateResultsFileTags(
            { slug: row.slug, tags: normalizedTags },
            selectedOrganisationId,
          );
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
          const invalidations: QueryFilters[] = [
            invalidateResultsFiles(selectedOrganisationId ?? null, selectedProjectId ?? null),
            invalidateExecutionRuns(selectedOrganisationId ?? null, selectedProjectId ?? null),
            invalidateGridQuery(gridPaths.resultsFiles),
            invalidateGridQuery(gridPaths.executionRuns),
            invalidateGridQuery(gridPaths.suiteRuns),
            invalidateGridQuery(gridPaths.testRuns),
          ];
          await invalidateQueries(invalidations);
          await queryClient.refetchQueries({ type: 'active' });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      invalidateQueries,
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteResultsFileMutation.isPending,
    deleteRowPendingId: deleteResultsFileMutation.isPending ? (deleteTarget?.id ?? null) : null,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onRestoreRow,
    restoreLoading: restoreResultsFileMutation.isPending,
    restoreRowPendingId,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId: updatingTagRowId,
  };
};
